// 1.5.45 — M insists on a Spare Key (HL-17; Alexander, 29 Oct: "Above $50, or after five transactions
// (in or out)"; "Protect your wallet. Make a Spare Key." — no question mark; "it comes back once a day
// until a key exists"; mock-up approved 30 Oct).
//
// The home's opening line on the day's first home becomes ***Protect your wallet. Make a Spare Key.***
// (the phrase the button, opening the Spare Key screen) once the wallet holds more than $50 (every
// pocket, in dollars) or has made five transactions in or out, until a Spare Key is on the wallet.
//
// What it knows, per wallet, in `m.spareAsk` (this browser's):
//   asked  — the local day the prompt was last shown (once a day);
//   keyDay — the local day the wallet's keys were last read from Arc; hasKey — what that read said;
//   txDay  — the local day the statement was last counted;       tx5    — five or more seen.
// Arc and the statement are read at most once a day, behind the home; until today's read of the keys
// is in, the prompt waits (never shown to a wallet that may already have a key).

import { getActivity } from "./api";
import { readKeys, type ContractReader } from "./sparekey/owners";
import { recordError } from "./diag";

export const SPARE_LINE = "Protect your wallet. Make a Spare Key.";
export const SPARE_ACT = "Make a Spare Key.";
export const SPARE_OVER_USD = 50;
export const SPARE_TX = 5;

const KEY = "m.spareAsk";
interface State { asked?: string; keyDay?: string; hasKey?: boolean; txDay?: string; tx5?: boolean }

const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; };
function all(): Record<string, State> { try { return JSON.parse(localStorage.getItem(KEY) ?? "{}") as Record<string, State>; } catch { return {}; } }
function get(a: string): State { return all()[a.toLowerCase()] ?? {}; }
function put(a: string, s: State): void {
  try { const m = all(); m[a.toLowerCase()] = s; localStorage.setItem(KEY, JSON.stringify(m)); } catch { /* storage refused: asked again next time */ }
}

// Shown on this page already (the line stays for the visit, whatever the day's mark says).
const shownNow = new Set<string>();

/** Should the home's opening line be the Spare Key prompt now? Synchronous: what is known. */
export function sparePromptDue(address: string, totalUsd: number): boolean {
  const a = address.toLowerCase();
  const s = get(a);
  if (s.keyDay !== today() || s.hasKey !== false) return false; // a key on the wallet, or not read today
  if (shownNow.has(a)) return true;
  if (s.asked === today()) return false;
  return totalUsd > SPARE_OVER_USD || s.tx5 === true;
}

/** The prompt was painted: once a day from here. */
export function sparePromptShown(address: string): void {
  const a = address.toLowerCase();
  if (shownNow.has(a)) return;
  shownNow.add(a);
  put(a, { ...get(a), asked: today() });
}

/** A Spare Key was just made, or deleted, in this browser: the prompt follows at once. */
export function spareKeyKnown(address: string, has: boolean): void {
  const a = address.toLowerCase();
  if (has) shownNow.delete(a);
  put(a, { ...get(a), keyDay: today(), hasKey: has });
}

/**
 * Today's reads, behind the home: the keys on Arc (once a day), the statement (once a day, only when the
 * balance alone doesn't decide). Calls `changed` when what it learnt may change the line. Never throws.
 */
export async function refreshSparePrompt(address: string, reader: ContractReader, totalUsd: () => number, changed: () => void): Promise<void> {
  const a = address.toLowerCase();
  const day = today();
  let s = get(a);
  if (s.asked === day && !shownNow.has(a)) return; // already asked today: nothing to read for
  let learnt = false;
  try {
    if (s.keyDay !== day) {
      const k = await readKeys(reader, address);
      s = { ...get(a), keyDay: day, hasKey: k.keys.some((x) => x.kind === "key") };
      put(a, s);
      learnt = true;
    }
    if (s.hasKey === false && !s.tx5 && s.txDay !== day && !(totalUsd() > SPARE_OVER_USD)) {
      const now = new Date();
      const months = [0, 1].map((back) => { const d = new Date(now.getFullYear(), now.getMonth() - back, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; });
      let n = 0;
      for (const m of months) {
        if (n >= SPARE_TX) break;
        const r = await getActivity(address, m, SPARE_TX);
        n += r.items?.length ?? 0;
      }
      s = { ...get(a), txDay: day, tx5: n >= SPARE_TX };
      put(a, s);
      learnt = true;
    }
  } catch (e) {
    recordError("sparekey/prompt", e);
  }
  if (learnt) changed();
}
